import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DataTable from 'components/ui/data-table/data-table';
import DropDown from 'components/VehicleTypeDropdown/VehicleTypeDropdown';
import rateService from '../../../services/rateService'; // Added API service import
import { createActionsColumn } from 'components/ui/data-table/columns';
import './PackageConfig.scss';

const PackageConfigurationForm = ({
  initialData = {},
  onChange,
  selectedLocation,
  selectedRideType,
  selectedCarType,
  contractId,
  rateCardId, // Added from original
  isUpdate, // Added from original
  onActivatePackage,
  isConfigureMode,
  isMultiDay,
}) => {
  const [basicConfig, setBasicConfig] = useState({
    baseRate: 0,
    surCharge: 0,
    platformCharge: 0,
  });

  const [packageConfig, setPackageConfig] = useState({
    hours: '',
    km: '',
    price: '',
    durationType: 'Hour',
    graceHour: '',
    graceType: 'Hour',
    extraPriceHour: '',
    graceKm: '',
    extraPriceKm: '',
    waitBuffer: '',
    waitPrice: '',
    advanceBookingCharge: '',
    nightAllowance: '',
    driverAllowance: '',
    earlyStartAllowance: '',
  });

  const [packages, setPackages] = useState([]);

  // Added from original PackageConfig.js
  const [packageOptions, setPackageOptions] = useState([]);
  const [updateRateCardById, setUpdateRateCardById] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageName, setPackageName] = useState('Package-1');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddPackageModalOpen, setIsAddPackageModalOpen] = useState(false);
  const [newPackageName, setNewPackageName] = useState('');
  const [newPackageDescription, setNewPackageDescription] = useState('');
  const [isAddAttempted, setIsAddAttempted] = useState(false);

  useEffect(() => {
    if (initialData) {
      setBasicConfig({
        baseRate: initialData.baseRate || 0,
        surCharge: initialData.surCharge || 0,
        platformCharge: initialData.platformCharge || 0,
      });

      // Added from original - handle packages initialization
      setPackages(initialData.packages || []);
    }
  }, [initialData]);

  const fetchDefaultPackageAndPopulate = async (
    selectedCarType,
    selectedLocation,
    selectedRideType,
  ) => {
    try {
      setIsEditMode(false);
      const options = await rateService.getPackageNameDropdown(contractId);
      setPackageOptions(options);

      const fullPackage = await rateService.getPackages({
        locationId: selectedLocation,
        tripType: selectedRideType,
        carType: selectedCarType,
        contractId,
        isMultiDay: isMultiDay,
      });

      setPackages(fullPackage.data?.data || []);

      if (selectedRideType === 'Outstation' || isMultiDay) {
        const row = fullPackage?.data?.data[0];
        const rateConfig = row?.rateConfig || {};

        if (row) {
          setIsEditMode(true);
          setUpdateRateCardById(row.id);
        }

        setBasicConfig({
          baseRate: row.baseRate || 0,
          surCharge: row.surCharge || 0,
          platformCharge: row.platformCharge || 0,
        });

        setPackageConfig({
          name: row.rateCardType?.name || '',
          hours: rateConfig.duration || '',
          durationType: (rateConfig.durationType || 'hour') === 'day' ? 'Day' : 'Hour',
          km: rateConfig.maxDistanceKms || '',
          price: rateConfig.cost || '',
          graceHour: rateConfig.extraHrs?.graceValue || '',
          graceType: (rateConfig.extraHrs?.unitType || 'Hour') === 'Min' ? 'Min' : 'Hour',
          extraPriceHour: rateConfig.extraHrs?.rate || '',
          graceKm: rateConfig.extraKM?.graceValue || '',
          extraPriceKm: rateConfig.extraKM?.rate || '',
          waitBuffer: rateConfig.waitingTime?.graceValue || '',
          waitPrice: rateConfig.waitingTime?.rate || '',
          nightAllowance: rateConfig.nightAllowance?.rate || '',
          earlyStartAllowance: rateConfig.earlyStartAllowance?.rate || '',
          driverAllowance: rateConfig.driverAllowance?.rate || '',
          advanceBookingCharge: rateConfig.additionalCharges?.[0]?.rate || '',
          nightAllowance: rateConfig.nightAllowance?.rate || '',
          earlyStartAllowance: rateConfig.earlyStartAllowance?.rate || '',
          driverAllowance: rateConfig.driverAllowance?.rate || '',
        });
      }
    } catch (error) {
      console.error('Error fetching package data:', error);
    }
  };

  // Added from original PackageConfig.js - Fetch package options and populate default data
  useEffect(() => {
    setPackageConfig((prev) => ({
      ...prev,
      durationType: selectedRideType == 'Outstation' ? 'Day' : 'Hour',
      hours: selectedRideType == 'Outstation' ? 1 : isMultiDay ? 8 : prev.hours,
    }));

    if (selectedLocation && selectedRideType && selectedCarType) {
      fetchDefaultPackageAndPopulate(selectedCarType, selectedLocation, selectedRideType);
    }
  }, [selectedLocation, selectedRideType, selectedCarType, isMultiDay]);

  // Added from original - Handle package selection change
  const handlePackageNameChange = async (value) => {
    try {
      const selected = packageOptions.find((opt) => opt.id === value);
      if (selected) {
        const populatedData = await rateService.getPackages({
          locationId: selectedLocation,
          tripType: selectedRideType,
          carType: selectedCarType,
          contractId,
          rateCardTypeId: selected.id,
        });

        if (populatedData) {
          setUpdateRateCardById(populatedData.id);
          const config = populatedData.rateConfig || {};

          setPackageConfig((prev) => ({
            ...prev,

            hours: config.duration || '',
            durationType:
              config.durationType === 'hour'
                ? 'Hour'
                : config.durationType === 'day'
                  ? 'Day'
                  : 'Hour',
            km: config.maxDistanceKms || '',
            price: config.cost || '',
            graceHour: config.extraHrs?.graceValue || '',
            extraPriceHour: config.extraHrs?.rate || '',
            graceKm: config.extraKM?.graceValue || '',
            extraPriceKm: config.extraKM?.rate || '',
            waitBuffer: config.waitingTime?.graceValue || '',
            waitPrice: config.waitingTime?.rate || '',
          }));

          // Update basic config if available
          setBasicConfig((prev) => ({
            ...prev,
            baseRate: populatedData.baseRate || prev.baseRate || 0,
            surCharge: populatedData.surCharge || prev.surCharge || 0,
            platformCharge: populatedData.platformCharge || prev.platformCharge || 0,
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching package by type:', error);
    }
  };

  const handleBasicConfigChange = (field, value) => {
    const updatedConfig = { ...basicConfig, [field]: value };
    setBasicConfig(updatedConfig);
    onChange?.(buildFormData(updatedConfig, packageConfig));
  };

  const handlePackageConfigChange = (field, value) => {
    const updatedConfig = { ...packageConfig, [field]: value };
    setPackageConfig(updatedConfig);

    // Handle package name change with API call
    if (field === 'name' && packageOptions.length > 0) {
      const selectedOption = packageOptions.find((opt) => opt.name === value);
      if (selectedOption) {
        handlePackageNameChange(selectedOption.id);
        return;
      }
    }

    onChange?.(buildFormData(basicConfig, updatedConfig));
  };

  const buildFormData = (basic, packageData) => {
    return {
      ...basic,
      ...packageData,
      packages,
    };
  };

  // Added from original - Save/Update package function
  const handleSavePackage = async () => {
    try {
      const meta = {
        locationId: selectedLocation,
        rideType: selectedRideType,
        carType: selectedCarType,
        contractId,
        isMultiDay: isMultiDay,
      };

      setIsSaving(true);

      // Build package data in the format expected by the API
      const packageData = {
        name: packageConfig.name,
        rateCardTypeId: selectedPackage?.value,
        baseRate: basicConfig.baseRate,
        surCharge: basicConfig.surCharge || 0,
        platformCharge: basicConfig.platformCharge || 0,
        duration: packageConfig.hours,
        durationType: packageConfig.durationType.toLowerCase(),
        maxDistanceKms: packageConfig.km,
        cost: packageConfig.price,
        extraKM: {
          rate: packageConfig.extraPriceKm,
          graceValue: packageConfig.graceKm,
          enabled: true,
        },
        extraHrs: {
          rate: packageConfig.extraPriceHour,
          graceValue: packageConfig.graceHour,
          unitType: packageConfig?.graceType || 'Hour',
          enabled: true,
        },
        waitingTime: {
          rate: packageConfig.waitPrice,
          graceValue: packageConfig.waitBuffer,
          enabled: true,
        },
        nightAllowance: {
          rate: packageConfig.nightAllowance,
          unitType: 'Rs',
        },
        earlyStartAllowance: {
          rate: packageConfig.earlyStartAllowance,
          unitType: 'Rs',
        },
        driverAllowance: {
          rate: packageConfig.driverAllowance,
          unitType: 'Rs',
        },
        additionalCharges: [
          {
            name: 'Advance Booking',
            label: 'Advance Booking',
            unitType: 'Rs',
            rate: parseFloat(packageConfig.advanceBookingCharge) || 0,
          },
        ],
      };

      const result = isEditMode
        ? await rateService.updatePackageRateCard(updateRateCardById, packageData, meta)
        : await rateService.savePackageRateCard(packageData, meta);

      if (result.success) {
        // Add to packages list
        const newPackage = {
          id: result.id || Date.now().toString(),
          ...packageConfig,
          backendRateCardId: result.id || updateRateCardById,
        };

        if (selectedRideType === 'Outstation' || isMultiDay) {
          fetchDefaultPackageAndPopulate(selectedCarType, selectedLocation, selectedRideType);
        } else {
          const fullPackage = await rateService.getPackages({
            locationId: selectedLocation,
            tripType: selectedRideType,
            carType: selectedCarType,
            contractId,
            isMultiDay: isMultiDay,
          });
          setPackages(fullPackage.data?.data || []);

          // Reset form
          setPackageConfig({
            name: '',
            hours: '',
            km: '',
            price: '',
            durationType: 'Hour',
            graceHour: '',
            graceType: 'Hour',
            extraPriceHour: '',
            graceKm: '',
            extraPriceKm: '',
            waitBuffer: '',
            waitPrice: '',
            advanceBookingCharge: '',
            nightAllowance: '',
            earlyStartAllowance: '',
            driverAllowance: '',
          });
          setIsEditMode(false);
        }
      } else {
        console.error('Failed to save package:', result.error);
      }
    } catch (error) {
      console.error('Error saving package:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Added from original - Delete package function
  const handleDeletePackage = async (packageId) => {
    try {
      const pkg = packages.find((p) => p.id === packageId);

      // Delete from backend if ID exists
      if (pkg?.id) {
        const result = await rateService.deleteRateCard(pkg.id);
        if (!result.success) {
          console.error('Failed to delete package from backend');
          return;
        }
      }

      // Remove from UI
      setPackages((prev) => prev.filter((p) => p.id !== packageId));
    } catch (error) {
      console.error('Error deleting package:', error);
    }
  };

  const getTripTypeFromRideType = (rideType) => {
    const mapping = {
      Ride: 1,
      Airport: 4,
      Rental: 5,
      Outstation: 7,
    };
    return mapping[rideType] || 3;
  };

  const handleAddNewPackage = async () => {
    setIsAddAttempted(true);
    if (!newPackageName.trim()) {
      return;
    }

    const result = await rateService.createNewPackageType(
      newPackageName,
      newPackageDescription,
      contractId,
    );

    if (result.success) {
      const newPkg = result.data;

      // Refresh dropdown
      const refreshed = await rateService.getPackageNameDropdown(contractId);
      setPackageOptions(refreshed);

      // Auto-select new package
      setSelectedPackage({ value: newPkg.id, label: newPkg.name });
      setPackageName(newPkg.name);

      // Reset modal state
      setNewPackageName('');
      setNewPackageDescription('');
      setIsAddPackageModalOpen(false);
    }
  };

  const hasInvalidValues = () => {
    const requiredFields = ['hours', 'km', 'price'];

    return requiredFields.some((field) => {
      const value = parseFloat(packageConfig[field]);
      return isNaN(value) || value <= 0 || !packageName || packageName.trim() == '';
    });
  };

  const isOutStation = isConfigureMode ? (selectedRideType !== 'Outstation' ? true : false) : true;

  // Determine if package table should be displayed
  const shouldDisplayPackageTable = () => {
    const multiDayInViewMode = isMultiDay && !isConfigureMode;
    const standardPackageInConfigMode = !isMultiDay && isConfigureMode;
    const viewOnlyMode = !isConfigureMode;

    return (
      packages.length > 0 &&
      isOutStation &&
      (multiDayInViewMode || standardPackageInConfigMode || viewOnlyMode)
    );
  };

  return (
    <div className="space-y-4">
      {isConfigureMode && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Left side - Select Package Dropdown - Hidden when isMultiDay is true */}
            {selectedRideType !== 'Outstation' && !isMultiDay && (
              <DropDown
                value={selectedPackage}
                onChange={(newValue) => {
                  setSelectedPackage(newValue);
                  setPackageName(newValue?.label);
                }}
                options={[
                  ...packageOptions.map((pkg, index) => ({
                    value: pkg.id,
                    label: pkg.name,
                  })),
                ]}
                placeholder="Select Package"
                disableClearable={true}
                className="responsive-input"
              />
            )}

            {/* Center - Base Fare, Surcharge, Platform Charge as Input Fields */}

            <TextField
              label="Base Fare"
              type="number"
              value={basicConfig.baseRate > 0 ? basicConfig.baseRate : ''}
              onChange={(e) => handleBasicConfigChange('baseRate', e.target.value)}
              onWheel={(e) => e.target.blur()}
              size="small"
              InputProps={{
                sx: {
                  height: '40px', // Match the Select dropdown height
                },
              }}
              fullWidth
              className="responsive-input"
            />

            <TextField
              label="Surcharge"
              type="number"
              value={basicConfig.surCharge > 0 ? basicConfig.surCharge : ''}
              onChange={(e) => handleBasicConfigChange('surCharge', e.target.value)}
              onWheel={(e) => e.target.blur()}
              size="small"
              fullWidth
              className="responsive-input"
            />

            <TextField
              label="Platform Charge"
              type="number"
              value={basicConfig.platformCharge > 0 ? basicConfig.platformCharge : ''}
              onChange={(e) => handleBasicConfigChange('platformCharge', e.target.value)}
              onWheel={(e) => e.target.blur()}
              size="small"
              fullWidth
              className="responsive-input"
            />
          </div>

          {/* Add new package link */}
          {selectedRideType !== 'Outstation' && !isMultiDay && isConfigureMode && (
            <div>
              <Button
                variant="text"
                sx={{
                  color: '#2563eb', // Tailwind's text-blue-600
                  textTransform: 'none',
                  fontSize: '0.875rem', // text-sm
                  padding: 0,
                  minWidth: 0,
                }}
                onClick={() => setIsAddPackageModalOpen(true)}
              >
                Add new package
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {isConfigureMode && (
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-md font-semibold text-gray-900 mb-6">
              {`${selectedRideType || 'Rental'} package configuration`}
            </h2>
            {selectedRideType !== 'Outstation' && (
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={packageConfig.durationType}
                  onChange={(e) => handlePackageConfigChange('durationType', e.target.value)}
                  sx={{ justifyContent: 'flex-end' }}
                >
                  {/* <FormControlLabel
                value="Minute"
                control={<Radio size="small" />}
                label="Minute"
              /> */}
                  <FormControlLabel
                    value="Hour"
                    control={<Radio size="small" />}
                    label="Hour"
                    className="mr-8"
                  />
                  {/* <FormControlLabel
                value="Day"
                control={<Radio size="small" />}
                label="Day"
                className="mr-8"
              /> */}
                </RadioGroup>
              </FormControl>
            )}
          </div>
        )}

        {isConfigureMode && (
          <div className="flex flex-wrap gap-4 mb-6 responsive-row">
            {selectedRideType !== 'Outstation' && !isMultiDay && (
              <TextField
                label="Package name"
                value={packageName}
                size="small"
                fullWidth
                required
                disabled={true}
                className="responsive-input"
              />
            )}
            <TextField
              label={`${
                packageConfig.durationType === 'Day'
                  ? 'Days'
                  : packageConfig.durationType === 'Minute'
                    ? 'Minutes'
                    : 'Hours'
              }`}
              type="number"
              value={packageConfig.hours}
              onChange={(e) => handlePackageConfigChange('hours', e.target.value)}
              onWheel={(e) => e.target.blur()}
              size="small"
              fullWidth
              required
              disabled={selectedRideType === 'Outstation' || isMultiDay ? true : false}
              className="responsive-input"
              // InputProps={{ readOnly: true }}
            />
            <TextField
              label={
                <span>
                  Max Distance(Km)<span style={{ color: 'red' }}> *</span>
                </span>
              }
              type="number"
              value={packageConfig.km}
              onChange={(e) => handlePackageConfigChange('km', e.target.value)}
              onWheel={(e) => e.target.blur()}
              size="small"
              fullWidth
              className="responsive-input"
            />
            <TextField
              label={
                <span>
                  Price<span style={{ color: 'red' }}> *</span>
                </span>
              }
              type="number"
              value={packageConfig.price}
              onChange={(e) => handlePackageConfigChange('price', e.target.value)}
              onWheel={(e) => e.target.blur()}
              size="small"
              fullWidth
              className="responsive-input"
            />
          </div>
        )}

        {/* Additional Package Configuration */}
        {isConfigureMode && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Additional package configuration
            </h3>

            <div className="additional-package-grid mb-6">
              <div className="border border-gray-200 rounded-lg p-4">
                {selectedRideType !== 'Outstation' && (
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={packageConfig.graceType}
                      onChange={(e) => handlePackageConfigChange('graceType', e.target.value)}
                    >
                      <FormControlLabel
                        value="Min"
                        control={<Radio size="small" />}
                        label="Minute"
                      />
                      <FormControlLabel
                        value="Hour"
                        control={<Radio size="small" />}
                        label="Hour"
                        className="mr-8"
                      />
                    </RadioGroup>
                  </FormControl>
                )}

                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <TextField
                    size="small"
                    value={packageConfig.graceHour}
                    onChange={(e) => handlePackageConfigChange('graceHour', e.target.value)}
                    label={`Grace ${packageConfig.graceType}`}
                    style={{ flex: '1', minWidth: '0' }}
                  />
                  <TextField
                    size="small"
                    value={packageConfig.extraPriceHour}
                    onChange={(e) => handlePackageConfigChange('extraPriceHour', e.target.value)}
                    label={`Extra price/${packageConfig.graceType}`}
                    style={{ flex: '1', minWidth: '0' }}
                  />
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    width: '100%',
                    height: '100%',
                    alignItems: 'flex-end',
                  }}
                >
                  <TextField
                    size="small"
                    value={packageConfig.graceKm}
                    onChange={(e) => handlePackageConfigChange('graceKm', e.target.value)}
                    label="Grace Km"
                    style={{ flex: '1', minWidth: '0' }}
                  />

                  <TextField
                    size="small"
                    value={packageConfig.extraPriceKm}
                    onChange={(e) => handlePackageConfigChange('extraPriceKm', e.target.value)}
                    label="Extra price/Km"
                    style={{ flex: '1', minWidth: '0' }}
                  />
                </div>
              </div>
              {/* 
            {selectedRideType !== 'Outstation' && <div className="border border-gray-200 rounded-lg p-4">
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  width: "100%",
                  height: "100%",
                  alignItems: "flex-end",
                }}
              >
                <TextField
                  size="small"
                  value={packageConfig.waitBuffer}
                  onChange={(e) =>
                    handlePackageConfigChange("waitBuffer", e.target.value)
                  }
                  label="Wait Buffer (Min)"
                  style={{ flex: "1", minWidth: "0" }}
                />

                <TextField
                  size="small"
                  value={packageConfig.waitPrice}
                  onChange={(e) =>
                    handlePackageConfigChange("waitPrice", e.target.value)
                  }
                  label="Wait Price (₹)"
                  style={{ flex: "1", minWidth: "0" }}
                />
              </div>
            </div>} */}

              <div className="border border-gray-200 rounded-lg p-4">
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    width: '100%',
                    height: '100%',
                    alignItems: 'flex-end',
                  }}
                >
                  <TextField
                    label="Advance booking charge"
                    value={packageConfig.advanceBookingCharge}
                    onChange={(e) =>
                      handlePackageConfigChange('advanceBookingCharge', e.target.value)
                    }
                    size="small"
                    fullWidth
                    className="additional-responsive-input"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </div>
              </div>
              {/* 
              {selectedRideType === "Outstation" && (
                <>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <TextField
                      label="Night Allowance (per night)"
                      value={packageConfig.nightAllowance}
                      onChange={(e) =>
                        handlePackageConfigChange(
                          "nightAllowance",
                          e.target.value,
                        )
                      }
                      size="small"
                      fullWidth
                      className="additional-responsive-input"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <TextField
                      label="Early Start Allowance"
                      value={packageConfig.earlyStartAllowance}
                      onChange={(e) =>
                        handlePackageConfigChange(
                          "earlyStartAllowance",
                          e.target.value,
                        )
                      }
                      size="small"
                      fullWidth
                      className="additional-responsive-input"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <TextField
                      label="Driver Allowance (per day)"
                      value={packageConfig.driverAllowance}
                      onChange={(e) =>
                        handlePackageConfigChange(
                          "driverAllowance",
                          e.target.value,
                        )
                      }
                      size="small"
                      fullWidth
                      className="additional-responsive-input"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                  </div>
                </>
              )} */}

              {(selectedRideType === 'Outstation' || selectedRideType === 'Rental') && (
                <>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <TextField
                      label={
                        selectedRideType === 'Outstation'
                          ? 'Night Allowance (per night)'
                          : 'Night Allowance'
                      }
                      value={packageConfig.nightAllowance}
                      onChange={(e) => handlePackageConfigChange('nightAllowance', e.target.value)}
                      size="small"
                      fullWidth
                      className="additional-responsive-input"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <TextField
                      label="Early Start Allowance"
                      value={packageConfig.earlyStartAllowance}
                      onChange={(e) =>
                        handlePackageConfigChange('earlyStartAllowance', e.target.value)
                      }
                      size="small"
                      fullWidth
                      className="additional-responsive-input"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                  </div>
                </>
              )}
              {selectedRideType === 'Outstation' && (
                <>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <TextField
                      label="Driver Allowance (per day)"
                      value={packageConfig.driverAllowance}
                      onChange={(e) => handlePackageConfigChange('driverAllowance', e.target.value)}
                      size="small"
                      fullWidth
                      className="additional-responsive-input"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Activate Package Button */}
        {isConfigureMode && (
          <Button
            type="button"
            variant="contained"
            onClick={handleSavePackage}
            disabled={hasInvalidValues() || isSaving}
            sx={{
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '8px',
              paddingY: '8px',
              paddingX: '24px',
              textTransform: 'none',
              fontWeight: 500,
              color: 'white',
              '&:hover': {
                backgroundColor: 'var(--brand-primary)',
              },
            }}
          >
            {isEditMode ? 'Update Package' : 'Activate Package'}
          </Button>
        )}

        {/* Package Table - Hidden when isMultiDay is true */}
        {shouldDisplayPackageTable() && (
          <div className="mt-4 max-w-full">
            <DataTable
              columns={[
                {
                  accessorKey: 'pack',
                  header: 'Pack',
                  cell: ({ row }) =>
                    selectedRideType !== 'Outstation'
                      ? isMultiDay
                        ? '8 Hour'
                        : `${row.original.rateCardType?.name}`
                      : '1 Day',
                },
                {
                  accessorKey: 'rateConfig.duration',
                  header: 'Duration',
                  cell: ({ row }) => {
                    const duration = row.original.rateConfig?.duration || 0;
                    const type = row.original.rateConfig?.durationType || 'hour';
                    const label = type.charAt(0).toUpperCase() + type.slice(1); // e.g., "Hour", "Minute", "Day"
                    return `${duration} ${label}`;
                  },
                },
                {
                  accessorKey: 'rateConfig.maxDistanceKms',
                  header: 'Km',
                  cell: ({ row }) => `${row.original.rateConfig?.maxDistanceKms || 0}km`,
                },
                {
                  accessorKey: 'rateConfig.cost',
                  header: 'Price',
                  cell: ({ row }) => row.original.rateConfig?.cost || 0,
                },
                {
                  accessorKey: 'rateConfig.extraKM.graceValue',
                  header: 'Grace Km',
                  cell: ({ row }) => row.original.rateConfig?.extraKM?.graceValue || 0,
                },
                {
                  accessorKey: 'rateConfig.extraKM.rate',
                  header: 'Extra/Km',
                  cell: ({ row }) => row.original.rateConfig?.extraKM?.rate || 0,
                },
                {
                  accessorKey: 'rateConfig.extraHrs.graceValue',
                  header: 'Grace Hr',
                  cell: ({ row }) => {
                    const graceValue = row.original.rateConfig?.extraHrs?.graceValue || 0;
                    const unitType = row.original.rateConfig?.extraHrs?.unitType;
                    return graceValue > 0
                      ? `${graceValue} ${unitType === 'Hour' ? 'Hr' : 'Min'}`
                      : 0;
                  },
                },
                {
                  accessorKey: 'rateConfig.extraHrs.rate',
                  header: 'Extra/Hr',
                  cell: ({ row }) => row.original.rateConfig?.extraHrs?.rate || 0,
                },

                ...(selectedRideType === 'Outstation' || selectedRideType === 'Rental'
                  ? [
                      {
                        id: 'nightAllowance',
                        accessorKey: 'rateConfig.nightAllowance.rate',
                        header: 'Night Allowance',
                        cell: ({ row }) => row.original.rateConfig?.nightAllowance?.rate || 0,
                      },
                      {
                        id: 'earlyStartAllowance',
                        accessorKey: 'rateConfig.earlyStartAllowance.rate',
                        header: 'Early Start Allowance',
                        cell: ({ row }) => row.original.rateConfig?.earlyStartAllowance?.rate || 0,
                      },
                      ...(selectedRideType === 'Outstation'
                        ? [
                            {
                              id: 'driverAllowance',
                              accessorKey: 'rateConfig.driverAllowance.rate',
                              header: 'Driver Allowance',
                              cell: ({ row }) =>
                                row.original.rateConfig?.driverAllowance?.rate || 0,
                            },
                          ]
                        : []),
                      {
                        id: 'advanceBookingCharge',
                        accessorKey: 'rateConfig.additionalCharges',
                        header: 'Adv Charge',
                        cell: ({ row }) => {
                          const additionalCharges = row.original.rateConfig?.additionalCharges;
                          return additionalCharges && additionalCharges.length > 0
                            ? additionalCharges[0].rate
                            : 0;
                        },
                      },
                    ]
                  : [
                      {
                        id: 'advanceBookingCharge',
                        accessorKey: 'rateConfig.additionalCharges',
                        header: 'Adv Charge',
                        cell: ({ row }) => {
                          const additionalCharges = row.original.rateConfig?.additionalCharges;
                          return additionalCharges && additionalCharges.length > 0
                            ? additionalCharges[0].rate
                            : 0;
                        },
                      },
                    ]),

                createActionsColumn(
                  [
                    {
                      label: 'Edit',
                      onClick: (row) => {
                        const rateConfig = row.rateConfig || {};
                        setIsEditMode(true);
                        setUpdateRateCardById(row.id || '');

                        setBasicConfig({
                          baseRate: row.baseRate || 0,
                          surCharge: row.surCharge || 0,
                          platformCharge: row.platformCharge || 0,
                        });

                        setPackageConfig({
                          name: row.rateCardType?.name || '',
                          hours: rateConfig.duration || '',
                          durationType:
                            (rateConfig.durationType || 'hour') === 'day' ? 'Day' : 'Hour',
                          km: rateConfig.maxDistanceKms || '',
                          price: rateConfig.cost || '',
                          graceHour: rateConfig.extraHrs?.graceValue || '',
                          graceType:
                            (rateConfig.extraHrs?.unitType || 'Hour') === 'Min' ? 'Min' : 'Hour',
                          extraPriceHour: rateConfig.extraHrs?.rate || '',
                          graceKm: rateConfig.extraKM?.graceValue || '',
                          extraPriceKm: rateConfig.extraKM?.rate || '',
                          waitBuffer: rateConfig.waitingTime?.graceValue || '',
                          waitPrice: rateConfig.waitingTime?.rate || '',
                          advanceBookingCharge: rateConfig.additionalCharges?.[0]?.rate || '',
                          nightAllowance: rateConfig.nightAllowance?.rate || '',
                          earlyStartAllowance: rateConfig.earlyStartAllowance?.rate || '',
                          driverAllowance: rateConfig.driverAllowance?.rate || '',
                        });

                        setSelectedPackage({
                          label: row.rateCardType?.name,
                          value: row.rateCardType?.id,
                        });

                        setPackageName(row.rateCardType?.name || '');
                      },
                    },
                    {
                      label: 'Delete',
                      onClick: (row) => handleDeletePackage(row.id),
                    },
                  ],
                  isConfigureMode,
                ),
              ].filter((col) => col !== null)}
              data={packages || []}
            />
          </div>
        )}

        {/* Add New Package Modal - Hidden when isMultiDay is true */}
        {!isMultiDay && (
          <Dialog open={isAddPackageModalOpen} onClose={() => setIsAddPackageModalOpen(false)}>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              Add new package
              <IconButton onClick={() => setIsAddPackageModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Package name"
                value={newPackageName}
                onChange={(e) => setNewPackageName(e.target.value)}
                fullWidth
                margin="normal"
                required
                error={isAddAttempted && !newPackageName.trim()}
                helperText={
                  isAddAttempted && !newPackageName.trim() ? 'Package name is required' : ''
                }
              />
              <TextField
                label="Description"
                value={newPackageDescription}
                onChange={(e) => setNewPackageDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                margin="normal"
              />
              <Button
                variant="contained"
                onClick={handleAddNewPackage}
                sx={{
                  mt: 2,
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '8px',
                  paddingY: '8px',
                  paddingX: '24px',
                  textTransform: 'none',
                  fontWeight: 500,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'var(--brand-primary)',
                  },
                }}
              >
                Add
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default PackageConfigurationForm;
